import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { Analytics } from "@vercel/analytics/react";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ManagerProtectedRoute } from "@/components/ManagerProtectedRoute";
import { GlobalAdminProtectedRoute } from "@/components/GlobalAdminProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AdminDealershipProvider } from "@/context/AdminDealershipContext";
import { ProfileProvider } from "@/context/ProfileContext";
import { ThemeProvider } from "@/context/ThemeProvider";
import { CallStateProvider } from "@/context/CallStateContext";
import { DepartmentProvider } from "@/context/DepartmentContext";
import { AnimatePresence, motion } from "framer-motion";
import { pageTransition } from "@/lib/motion";
import React, { Suspense, useEffect } from "react";
import { BottomNav } from "./components/BottomNav";
import { InstallPrompt } from "./components/InstallPrompt";
import { DepartmentSwitcher } from "./components/training/DepartmentSwitcher";
import { useMobileRouteContext } from "@/hooks/useInstalledAppContext";
import { MOBILE_APP_ROUTE_POLICY } from "@/lib/mobileRoutePolicy";
import { shouldAllowMobileTrainEntry } from "@/lib/mobileTrainEntry";

// Wrap Routes with Sentry for parameterized transaction names (e.g. /dashboard/:tab)
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

// Lazy-loaded page components for route-level code splitting
const Index = React.lazy(() => import("./pages/Index"));
const Product = React.lazy(() => import("./pages/Product"));
const Solutions = React.lazy(() => import("./pages/Solutions"));
const Resources = React.lazy(() => import("./pages/Resources"));
const Contact = React.lazy(() => import("./pages/Contact"));
const Privacy = React.lazy(() => import("./pages/Privacy"));
const Terms = React.lazy(() => import("./pages/Terms"));
const Train = React.lazy(() => import("./pages/Train"));
const Practice = React.lazy(() => import("./pages/Practice"));
const Me = React.lazy(() => import("./pages/Me"));
const Settings = React.lazy(() => import("./pages/Settings"));
const SessionHistory = React.lazy(() => import("./pages/SessionHistory"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const Onboarding = React.lazy(() => import("./pages/Onboarding"));
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const RepDashboard = React.lazy(() => import("./pages/RepDashboard"));
const RepCallsPageLazy = React.lazy(() =>
  import("./components/dashboard/calls/RepCallsPage").then((m) => ({ default: m.RepCallsPage }))
);
const Games = React.lazy(() => import("./pages/Games"));
const TrainingPlan = React.lazy(() =>
  import("./pages/TrainingPlan").then((m) => ({ default: m.TrainingPlan }))
);
const Pricing = React.lazy(() => import("./pages/Pricing"));
const ActivateAccount = React.lazy(() => import("./pages/ActivateAccount"));
const SignIn = React.lazy(() => import("./pages/SignIn"));
const SignUp = React.lazy(() => import("./pages/SignUp"));
const AuthCallback = React.lazy(() => import("./pages/AuthCallback"));
const ResetPassword = React.lazy(() => import("./pages/ResetPassword"));
const ChoosePlan = React.lazy(() => import("./pages/ChoosePlan"));
const SubscriptionSuccess = React.lazy(() => import("./pages/SubscriptionSuccess"));
const SubscriptionCancel = React.lazy(() => import("./pages/SubscriptionCancel"));
const AcceptInvite = React.lazy(() => import("./pages/AcceptInvite"));
const SetupDealership = React.lazy(() => import("./pages/SetupDealership"));
const GlobalAdminHub = React.lazy(() => import("./pages/GlobalAdminHub"));

const PUBLIC_MARKETING_ROUTES = new Set(["/", "/product", "/solutions", "/resources", "/pricing", "/contact", "/privacy", "/terms"]);

const isPublicMarketingRoute = (pathname: string) => PUBLIC_MARKETING_ROUTES.has(pathname);

const PageLoader = (): React.ReactElement => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is fresh for 30 seconds - won't refetch during this time
      staleTime: 30 * 1000,
      // Cache data for 5 minutes after component unmounts
      gcTime: 5 * 60 * 1000,
      // Retry failed requests 2 times with exponential backoff
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
      // Don't refetch on window focus for better UX
      refetchOnWindowFocus: false,
      // Refetch on reconnect to ensure fresh data
      refetchOnReconnect: true,
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
    },
  },
});

const MobileAppTrainRoute = () => {
  const isMobileRoute = useMobileRouteContext();
  const location = useLocation();

  if (isMobileRoute && !shouldAllowMobileTrainEntry(location.search)) {
    return <Navigate to={MOBILE_APP_ROUTE_POLICY.preferredPracticePath} replace />;
  }

  return (
    <ErrorBoundary>
      <Train />
    </ErrorBoundary>
  );
};

const AnimatedRoutes = () => {
  const location = useLocation();
  const isPublicRoute = isPublicMarketingRoute(location.pathname);
  
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        variants={pageTransition}
        initial="hidden"
        animate="visible"
        exit="exit"
        className={`min-h-screen flex flex-col ${isPublicRoute ? "" : "pb-bottom-nav md:pb-0"}`}
      >
        <Suspense fallback={<PageLoader />}>
        <SentryRoutes location={location} key={location.pathname}>
          <Route path="/" element={<Index />} />
          <Route path="/product" element={<Product />} />
          <Route path="/solutions" element={<Solutions />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/auth/sign-in" element={<SignIn />} />
          <Route path="/auth/sign-up/*" element={<SignUp />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/reset-password" element={<ResetPassword />} />
          <Route path="/auth/*" element={<Navigate to="/auth/sign-in?source=auth-root" replace />} />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute requireOnboarding={false} requireSubscription={false}>
                <ErrorBoundary>
                  <Onboarding />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          {/* Train page handles its own auth - allows free sessions without login */}
          <Route path={MOBILE_APP_ROUTE_POLICY.trainEntryPath} element={<MobileAppTrainRoute />} />
          <Route path="/practice" element={
            <ProtectedRoute>
              <ErrorBoundary>
                <Practice />
              </ErrorBoundary>
            </ProtectedRoute>
          } />
          <Route path="/my-dashboard" element={
            <ProtectedRoute>
              <ErrorBoundary>
                <RepDashboard />
              </ErrorBoundary>
            </ProtectedRoute>
          } />
          <Route path="/my-dashboard/calls" element={
            <ProtectedRoute>
              <ErrorBoundary>
                <RepCallsPageLazy />
              </ErrorBoundary>
            </ProtectedRoute>
          } />
          <Route path="/games" element={
            <ProtectedRoute requireSubscription={false}>
              <ErrorBoundary>
                <Games />
              </ErrorBoundary>
            </ProtectedRoute>
          } />
          <Route path="/training-plan" element={
            <ProtectedRoute>
              <ErrorBoundary>
                <TrainingPlan />
              </ErrorBoundary>
            </ProtectedRoute>
          } />
          <Route path="/me" element={
            <ProtectedRoute>
              <ErrorBoundary>
                <Me />
              </ErrorBoundary>
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <ErrorBoundary>
                <Settings />
              </ErrorBoundary>
            </ProtectedRoute>
          } />
          <Route path="/sessions" element={
            <ProtectedRoute>
              <ErrorBoundary>
                <SessionHistory />
              </ErrorBoundary>
            </ProtectedRoute>
          } />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/activate" element={<ActivateAccount />} />
          <Route 
            path="/choose-plan" 
            element={
              <ProtectedRoute requireOnboarding={true} requireSubscription={false}>
                <ErrorBoundary>
                  <ChoosePlan />
                </ErrorBoundary>
              </ProtectedRoute>
            } 
          />
          <Route path="/subscription-success" element={<SubscriptionSuccess />} />
          <Route path="/subscription-cancel" element={<SubscriptionCancel />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route 
            path="/setup-dealership" 
            element={
              <ProtectedRoute requireOnboarding={false} requireSubscription={false}>
                <ErrorBoundary>
                  <SetupDealership />
                </ErrorBoundary>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/*" 
            element={
              <ProtectedRoute>
                <ManagerProtectedRoute>
                  <ErrorBoundary>
                    <Dashboard />
                  </ErrorBoundary>
                </ManagerProtectedRoute>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/global-admin/*" 
            element={
              <ProtectedRoute>
                <GlobalAdminProtectedRoute>
                  <ErrorBoundary>
                    <GlobalAdminHub />
                  </ErrorBoundary>
                </GlobalAdminProtectedRoute>
              </ProtectedRoute>
            } 
          />
          <Route
            path="/manager"
            element={<Navigate to="/dashboard/live" replace />}
          />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </SentryRoutes>
        </Suspense>
      </motion.div>
    </AnimatePresence>
  );
};

const AppChrome = () => {
  const location = useLocation();
  const isPublicRoute = isPublicMarketingRoute(location.pathname);

  if (isPublicRoute) return null;

  return (
    <>
      <SentryFeedbackWidget pathname={location.pathname} />
      <BottomNav />
      <InstallPrompt />
    </>
  );
};

const SentryFeedbackWidget = ({ pathname }: { pathname: string }) => {
  useEffect(() => {
    if (typeof window === "undefined" || typeof Sentry.getFeedback !== "function") return;
    if (window.matchMedia?.("(max-width: 767px)").matches) return;

    const feedback = Sentry.getFeedback();
    if (!feedback || typeof feedback.createWidget !== "function") return;

    const widget = feedback.createWidget({
      triggerLabel: "Report a Bug",
      formTitle: "Report a Bug",
      submitButtonLabel: "Send Report",
      messagePlaceholder: "What happened? What did you expect?",
    });
    widget.appendToDom();

    return () => {
      widget.removeFromDom();
    };
  }, [pathname]);

  return null;
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ProfileProvider>
        <AdminDealershipProvider>
          <DepartmentProvider>
          <CallStateProvider>
          <ThemeProvider>
            <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <AnimatedRoutes />
              <AppChrome />
              <DepartmentSwitcher />
            </BrowserRouter>
            <Analytics />
            </TooltipProvider>
          </ThemeProvider>
          </CallStateProvider>
          </DepartmentProvider>
        </AdminDealershipProvider>
      </ProfileProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
